import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { AlertCircle, ArrowLeft, ArrowRight, KeyRound, Mail, ShieldCheck, Smartphone } from 'lucide-react';
import { useAuth } from 'app/javascript/contexts/AuthContext';
import { APP_OWNER_EMAIL } from 'app/javascript/constants/admin';
import { ROUTES } from 'app/javascript/constants/routes';

// The only sign-in left on the web: the app owner, by e-mail code, into the
// admin dashboard. Everyone else signs in through the native app, and the
// AuthProvider drops any web session whose /me is not app_admin.
export default function AdminLogin() {
  const { currentUser, requestEmailCode, verifyEmailCode } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (currentUser?.appAdmin) navigate({ to: ROUTES.ADMIN, replace: true });
  }, [currentUser?.appAdmin, navigate]);

  const isOwnerEmail = email.trim().toLowerCase() === APP_OWNER_EMAIL;

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isOwnerEmail) {
      setError('Este acesso é restrito ao administrador. Entre pelo app BlackBelts.');
      return;
    }
    try {
      setError('');
      setLoading(true);
      await requestEmailCode(APP_OWNER_EMAIL);
      setCodeSent(true);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Falha ao enviar código de acesso.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length < 6) return;
    try {
      setError('');
      setLoading(true);
      await verifyEmailCode(APP_OWNER_EMAIL, code);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Código inválido ou expirado.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'appearance-none block w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl placeholder-slate-500 text-white focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400/50 sm:text-sm transition-all duration-200';
  const buttonClass =
    'group relative w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-yellow-400 text-slate-900 font-bold hover:bg-yellow-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <main className="min-h-screen bg-slate-950 text-white flex items-start justify-center p-4 pt-10 sm:pt-16">
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8 shadow-2xl">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-yellow-400 flex items-center justify-center shadow-lg shadow-yellow-400/20">
              {codeSent ? <KeyRound className="w-8 h-8 text-slate-900" /> : <ShieldCheck className="w-8 h-8 text-slate-900" />}
            </div>
          </div>

          <div className="text-center space-y-2 mb-8">
            <p className="text-xs font-bold uppercase tracking-[3px] text-yellow-400">Administração</p>
            <h1 className="font-bebas text-3xl text-white">{codeSent ? 'Digite o código' : 'Entrar'}</h1>
            {codeSent ? (
              <>
                <p className="text-slate-400 text-sm">Enviamos um código de acesso para:</p>
                <p className="text-yellow-400 font-medium bg-yellow-400/10 py-1 px-3 rounded-full inline-block text-sm border border-yellow-400/20">
                  {APP_OWNER_EMAIL}
                </p>
              </>
            ) : (
              <p className="text-slate-400 text-sm">Acesso restrito ao administrador, por código enviado ao e-mail.</p>
            )}
          </div>

          {error && (
            <div role="alert" className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-200">{error}</p>
            </div>
          )}

          {codeSent ? (
            <form className="space-y-4" onSubmit={handleVerify}>
              <label htmlFor="admin-code" className="sr-only">
                Código de acesso
              </label>
              <input
                id="admin-code"
                name="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                maxLength={6}
                autoFocus
                className={`${inputClass} text-center tracking-[0.45em] font-mono`}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
              <button type="submit" disabled={loading || code.length < 6} className={buttonClass}>
                {loading ? 'Verificando...' : 'Entrar'}
                {!loading && <ArrowRight className="w-4 h-4" />}
              </button>
              <button
                type="button"
                onClick={() => {
                  setCodeSent(false);
                  setCode('');
                  setError('');
                }}
                className="flex items-center justify-center gap-2 w-full text-sm font-medium text-slate-400 hover:text-white transition-colors py-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Reenviar código
              </button>
            </form>
          ) : (
            <form className="space-y-4" onSubmit={handleRequest}>
              <label htmlFor="admin-email" className="sr-only">
                E-mail do administrador
              </label>
              <input
                id="admin-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                autoFocus
                className={inputClass}
                placeholder="E-mail do administrador"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button type="submit" disabled={loading} className={buttonClass}>
                {loading ? 'Enviando...' : 'Enviar código'}
                {!loading && <Mail className="w-4 h-4" />}
              </button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-slate-500">
          Alunos e academias entram pelo app.{' '}
          <Link to={ROUTES.APP_ONLY} className="inline-flex items-center gap-1 text-yellow-400 hover:text-yellow-300 font-medium">
            <Smartphone className="w-4 h-4" />
            Saiba como
          </Link>
        </p>
      </div>
    </main>
  );
}
